/**
 * Константы приложения - единый источник правды
 */
// ============================================
// VALIDATION LIMITS
// ============================================
export const VALIDATION_LIMITS = {
    CLIENT_NAME_MIN: 1,
    CLIENT_NAME_MAX: 200,
    SERVICE_NAME_MIN: 1,
    SERVICE_NAME_MAX: 100,
    COMMENT_MAX: 1000,
    DESCRIPTION_MAX: 500,
    /** Минимальная длина сырого ввода до refine isValidPhone (E.164 короче не бывает по смыслу). */
    PHONE_MIN: 8,
    /** E.164: + и до 15 цифр */
    PHONE_MAX: 20,
    EMAIL_MAX: 255,
    VK_USERNAME_MAX: 100,
    TELEGRAM_USERNAME_MAX: 100,
    TITLE_MAX: 100,
    ADDRESS_MAX: 200,
    /** Цена: минимум 0, максимум (рубли) */
    PRICE_MIN: 0,
    PRICE_MAX: 99999999,
};
export { OVERLAY_MODES, OVERLAY_DEFAULT_MODE, FORM_OVERLAY_LAYOUT, DIALOG_CARD_MAX_WIDTH_CSS, } from './overlay.js';
// ============================================
// VALIDATION PATTERNS (один источник для email, url, username)
// ============================================
/** Регулярные выражения для валидации полей (индустриальные/платформенные правила) */
export const VALIDATION_PATTERNS = {
    /**
     * Email только в ASCII (латиница в домене и TLD): как на практике у SMTP/DNS без IDN.
     * Отсекает опечатки с кириллицей (например .coь вместо .com) и прочий Unicode.
     */
    EMAIL: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,63}$/,
    /** VK URL: vk.com/username или vk.com/id123 */
    VK_URL: /^https?:\/\/(www\.)?vk\.com\/[\w.-]+$/i,
    /** Telegram username: 5–32 символа, a-zA-Z0-9_ (официальные правила) */
    TELEGRAM_USERNAME: /^[a-zA-Z0-9_]{5,32}$/,
    /** Время HH:MM (0-23, 0-59) */
    TIME: /^\d{1,2}:\d{2}$/,
};
// ============================================
// BUSINESS LIMITS
// ============================================
export const BUSINESS_LIMITS = {
    SERVICES_PER_APPOINTMENT_MIN: 1,
    /** Лимит по количеству услуг снят: валидация только по длительности слота (freeInterval ≥ D). Верхняя граница для схемы. */
    SERVICES_PER_APPOINTMENT_MAX: 99,
    PRODUCTS_PER_APPOINTMENT_MAX: 20,
    MIN_INTERVAL_DURATION_MINUTES: 15,
};
// ============================================
// UI CONSTANTS
// ============================================
export const UI_CONSTANTS = {
    EMPTY_FIELD_TEXT: 'не заполнено',
    DESCRIPTION_PREVIEW_LENGTH: 150,
    TOAST_DURATION_MS: 4000,
    TOAST_DURATION_ERROR_MS: 5000,
    TOAST_DURATION_SUCCESS_MS: 2000,
};
// ============================================
// BILLING (единый источник для backend и frontend)
// ============================================
export const BILLING_CONSTANTS = {
    /** Дней в месяце для расчёта per-day цены */
    DAYS_PER_MONTH: 30,
    /** Скидка за 3 месяца (0.9 = 10%) */
    DISCOUNT_3M: 0.9,
    /** Скидка за год (0.8 = 20%) */
    DISCOUNT_YEAR: 0.8,
    /** Длительность signup promo (дней). Единственный источник правды для backend и UI. */
    SIGNUP_PROMO_DAYS: 7,
    /** Пути возврата после оплаты (эквайринг). Backend редиректит сюда; gate перенаправляет в app. */
    PAYMENT_RETURN_SUCCESS_PATH: '/payment/success',
    PAYMENT_RETURN_FAIL_PATH: '/payment/fail',
    /** Пути экранов результата внутри приложения (после gate). Единственный источник для роутов и gate. */
    PAYMENT_APP_RESULT_PATH: '/app/payment-result',
    PAYMENT_APP_FAIL_PATH: '/app/payment-fail',
    /** Способы оплаты на странице Точки: карта и СБП. Одна кнопка «Купить» — выбор на стороне эквайринга. */
    ACQUIRING_PAYMENT_MODES: ['card', 'sbp'],
    /** Базовый курс для пересчёта рублёвой цены в Telegram Stars. 1 Star ~= 1.8 RUB. */
    TELEGRAM_STARS_RUB_PER_STAR: 1.8,
};
/** Ключи планов (BASIC, PRO). Контракт с Prisma TariffPlan и API BillingPlan. */
export const BILLING_PLAN_KEYS = ['BASIC', 'PRO'];
// ============================================
// BILLING PERIODS (единственный источник правды)
// ============================================
/** Порядок периодов (UI и API). */
export const BILLING_PERIODS = ['month', '3months', 'year'];
/** Месяцы в периоде (включая legacy 6months). */
export const BILLING_PERIOD_MONTHS = {
    month: 1,
    '3months': 3,
    '6months': 6,
    year: 12,
};
export const BILLING_PERIODS_WITH_LEGACY = Object.keys(BILLING_PERIOD_MONTHS);
/** Ранг периода для сравнения (month < 3months < year). */
export const BILLING_PERIOD_RANK = {
    month: 0,
    '3months': 1,
    year: 2,
};
export const normalizeBillingPeriod = (value) => {
    if (value === 'month' || value === '3months' || value === 'year')
        return value;
    return null;
};
export const getBillingPeriodRank = (value) => BILLING_PERIOD_RANK[value];
export const convertRublesToTelegramStars = (amountRub) => {
    const rate = BILLING_CONSTANTS.TELEGRAM_STARS_RUB_PER_STAR;
    if (!Number.isFinite(amountRub) || amountRub <= 0)
        return 0;
    return Math.ceil(amountRub / rate);
};
export const normalizeBillingPeriodWithLegacy = (value) => {
    if (!value)
        return null;
    return (value in BILLING_PERIOD_MONTHS ? value : null);
};
// ============================================
// ANALYTICS PERIODS
// ============================================
export const ANALYTICS_PERIODS = ['week', 'month', 'year'];
// ============================================
// VK PAY CONSTANTS (единый источник для backend и frontend)
// ============================================
/** Bridge метод для открытия формы оплаты VK */
export const VK_BRIDGE_PAY_METHOD = 'VKWebAppOpenPayForm';
/** Валюта для VK платежей */
export const VK_PAY_CURRENCY = 'RUB';
// ============================================
// HTTP/API CONSTANTS
// ============================================
export const API_CONSTANTS = {
    REQUEST_TIMEOUT_MS: 7000, // 7 секунд: компромисс между мобильной сетью и быстрым offline UX
    /** Публичная запись: дольше default — cold start + слоты на слабой сети. */
    BOOKING_REQUEST_TIMEOUT_MS: 15000,
    OFFLINE_TOAST_DURATION_MS: 3000,
    COPY_NOTIFICATION_POSITION: -9999, // для скрытия textarea при копировании
};
// ============================================
// BOT & NOTIFICATIONS
// ============================================
/**
 * Лимиты для записей и клиентов в ботах
 */
export const BOT_LIMITS = {
    /** Количество записей показываемых в списке бота */
    LIST_LIMIT: 5,
    /** Минимальная длина короткого кода для управления записью */
    SHORT_CODE_MIN: 4,
};
/**
 * Доступные часы для напоминаний
 */
export const ALLOWED_REMINDER_HOURS = [1, 2, 3, 6, 12, 24];
/**
 * Дефолтное время напоминания (часы до записи)
 */
export const DEFAULT_REMINDER_HOURS = 24;
/**
 * Временные лимиты
 */
export const TIME_LIMITS = {
    /** Максимальное количество минут в дне (23:59) */
    MAX_MINUTES_IN_DAY: 1439,
    /** Максимальная длина заметок клиента (символы) */
    MAX_NOTES_LENGTH: 2000,
    /** Подпись к постоянному перерыву в шаблоне недели (таймлайн) */
    MAX_WEEK_TEMPLATE_BREAK_NOTE_LENGTH: 200,
    /** Сколько постоянных перерывов максимум в одном дне шаблона (web/mobile одинаково) */
    MAX_WEEK_TEMPLATE_BREAKS_PER_DAY: 12,
};
// ============================================
// BUSINESS DEFAULTS (дефолтные значения для онбординга)
// ============================================
/**
 * Дефолтные значения для нового мастера
 */
export const BUSINESS_DEFAULTS = {
    /** Дефолтная временная зона системы, если её не передали пользователь или браузер */
    DEFAULT_TIMEZONE: 'Europe/Moscow',
    /** Дефолтное время начала работы (минуты от начала дня, 09:00) */
    WORK_START_MIN: 540,
    /** Дефолтное время конца работы (минуты от начала дня, 18:00) */
    WORK_END_MIN: 1080,
    /** Дефолтная цена услуги "Стрижка" (рубли) */
    SERVICE_HAIRCUT_PRICE: 1500,
    /** Дефолтная длительность услуги "Стрижка" (минуты) */
    SERVICE_HAIRCUT_DURATION: 60,
    /** Дефолтная цена услуги "Окрашивание" (рубли) */
    SERVICE_COLORING_PRICE: 2000,
    /** Дефолтная длительность услуги "Окрашивание" (минуты) */
    SERVICE_COLORING_DURATION: 90,
    /** Дефолтная цена услуги "Укладка" (рубли) */
    SERVICE_STYLING_PRICE: 1000,
    /** Дефолтная длительность услуги "Укладка" (минуты) */
    SERVICE_STYLING_DURATION: 30,
    /** Название клиента для перерыва (используется при создании break appointments) */
    BREAK_CLIENT_NAME: 'Перерыв',
};
//# sourceMappingURL=index.js.map